// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react'
import { FileText, ChevronDown, ChevronUp, CheckCircle, Loader2, AlertCircle } from 'lucide-react'
import { AnalysisReport } from './AnalysisReport/AnalysisReport'

export interface AnalysisResult {
  documentId: string
  documentName: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  analysisData?: any
  error?: string
  tokenCount?: number
}

interface IndividualAnalysisTrayProps {
  title: string
  analyses: Map<string, AnalysisResult>
  isProcessing: boolean
}

export default function IndividualAnalysisTray({ 
  title, 
  analyses,
  isProcessing 
}: IndividualAnalysisTrayProps) {
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set())

  const toggleExpand = (docId: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev)
      if (next.has(docId)) {
        next.delete(docId)
      } else {
        next.add(docId)
      }
      return next
    })
  }

  const analysesArray = Array.from(analyses.values())
  const completedCount = analysesArray.filter(a => a.status === 'completed').length
  const totalCount = analysesArray.length
  const totalTokens = analysesArray.reduce((sum, a) => sum + (a.tokenCount || 0), 0)

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />
      default:
        return <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
    }
  }

  // Calculate summary stats from analysis data
  const getSummaryStats = (analysisData: any) => {
    if (!analysisData) return null
    
    const data = analysisData.result?.analysis_data || analysisData
    const totalProblems = data.basic_info?.total_problems || data.problem_analysis?.length || 0
    const conceptsCount = data.concepts_covered?.length || 0
    
    // Calculate average difficulty
    let avgDifficulty = 0
    if (data.concepts_covered && data.concepts_covered.length > 0) {
      const sum = data.concepts_covered.reduce((acc: number, c: any) => acc + (c.difficulty_level || 0), 0)
      avgDifficulty = sum / data.concepts_covered.length
    } else if (data.problem_analysis && data.problem_analysis.length > 0) {
      const sum = data.problem_analysis.reduce((acc: number, p: any) => acc + (p.difficulty_level || 0), 0)
      avgDifficulty = sum / data.problem_analysis.length
    }
    
    return {
      totalProblems,
      conceptsCount,
      avgDifficulty: avgDifficulty > 0 ? avgDifficulty.toFixed(1) : 'N/A'
    }
  }

  return (
    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-gray-800">{title}</h4>
          <div className="text-xs text-gray-600 flex items-center gap-3">
            <span className="font-medium">
              {completedCount}/{totalCount} analyzed
            </span>
            {totalTokens > 0 && (
              <span className="font-mono bg-white px-2 py-1 rounded border border-gray-200">
                {totalTokens.toLocaleString()} tokens
              </span>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        {isProcessing && totalCount > 0 && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Analysis list */}
      <div className="max-h-[600px] overflow-y-auto">
        {analysesArray.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">No documents to analyze</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {analysesArray.map((analysis) => {
              const isExpanded = expandedDocs.has(analysis.documentId)
              const summaryStats = analysis.status === 'completed' ? getSummaryStats(analysis.analysisData) : null
              
              return (
                <div key={analysis.documentId} className="border border-gray-200 rounded-lg overflow-hidden m-3">
                  {/* Document header */}
                  <div className="bg-gray-50 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getStatusIcon(analysis.status)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {analysis.documentName}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {analysis.status === 'completed' && 'Analysis complete'}
                          {analysis.status === 'processing' && 'Analyzing...'}
                          {analysis.status === 'failed' && 'Analysis failed'}
                          {analysis.status === 'pending' && 'Pending'}
                          {analysis.tokenCount && analysis.status === 'completed' && (
                            <> • {analysis.tokenCount.toLocaleString()} tokens</>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expand/Collapse button */}
                    {analysis.status === 'completed' && analysis.analysisData && (
                      <button
                        onClick={() => toggleExpand(analysis.documentId)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 ml-2"
                      >
                        {isExpanded ? (
                          <>
                            Hide Details
                            <ChevronUp className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            View Analysis
                            <ChevronDown className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Summary Stats - Always visible for completed analyses */}
                  {analysis.status === 'completed' && summaryStats && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="text-center p-2 bg-blue-50 rounded">
                          <div className="font-semibold text-blue-900">{summaryStats.totalProblems}</div>
                          <div className="text-xs text-blue-700">Problems</div>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded">
                          <div className="font-semibold text-green-900">{summaryStats.conceptsCount}</div>
                          <div className="text-xs text-green-700">Concepts</div>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded">
                          <div className="font-semibold text-purple-900">{summaryStats.avgDifficulty}</div>
                          <div className="text-xs text-purple-700">Avg Difficulty</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {analysis.status === 'failed' && analysis.error && (
                    <div className="p-3 bg-red-50 border-t border-red-200">
                      <p className="text-xs text-red-600">{analysis.error}</p>
                    </div>
                  )}

                  {/* Expanded Full Analysis - Uses AnalysisReport component */}
                  {isExpanded && analysis.analysisData && (
                    <div className="p-4 bg-white border-t border-gray-200 max-h-96 overflow-y-auto">
                      <AnalysisReport analysisData={analysis.analysisData} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
